import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import { Button } from "@shared/components/ui/button";
import { Textarea } from "@shared/components/ui/textarea";
import { Label } from "@shared/components/ui/label";
import { Badge } from "@shared/components/ui/badge";
import { Package } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import * as packagesService from "../services/packagesService";

const PICKUP_STATUS_OPTIONS = [
  {
    value: "pending",
    label: "Pending",
    description: "Waiting for warehouse approval",
    color: "bg-gray-500",
  },
  {
    value: "picked_up",
    label: "Picked Up",
    description: "Packages collected from warehouse",
    color: "bg-blue-500",
  },
  {
    value: "in_transit",
    label: "In Transit",
    description: "On the way to warehouse",
    color: "bg-yellow-500",
  },
  {
    value: "signed",
    label: "Delivered to Warehouse",
    description: "Successfully delivered to warehouse",
    color: "bg-green-500",
  },
  {
    value: "return_collection",
    label: "Failed/Returned",
    description: "Failed to deliver or returned to warehouse",
    color: "bg-red-500",
  },
];

const LivreurPickupStatusUpdateDialog = ({ pickupData, trigger }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(pickupData.status);
  const [note, setNote] = useState("");

  const updatePickupStatusMutation = useMutation({
    mutationFn: (data) =>
      packagesService.updatePickupStatusByLivreur(pickupData._id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["livreur-pickups"] });
      setOpen(false);
      setNote("");
      toast.success(t("Pickup status updated successfully"));
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("Failed to update pickup status")
      );
    },
  });

  const handleStatusUpdate = () => {
    if (!selectedStatus) {
      toast.error(t("Please select a status"));
      return;
    }

    updatePickupStatusMutation.mutate({
      status: selectedStatus,
      note: note.trim(),
    });
  };

  const selectedStatusOption = PICKUP_STATUS_OPTIONS.find(
    (option) => option.value === selectedStatus
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5" />
            {t("Update Pickup Status")}
          </DialogTitle>
          <DialogDescription>
            {t("Update the status of this pickup to warehouse")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Status */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("Current Status")}</Label>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={`${
                  PICKUP_STATUS_OPTIONS.find(
                    (option) => option.value === pickupData.status
                  )?.color || "bg-gray-500"
                } text-white border-0`}
              >
                {pickupData.status?.replace(/_/g, " ").toUpperCase()}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Pickup ID: {pickupData._id}
              </span>
            </div>
          </div>

          {/* Pickup Details */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">{t("Pickup Details")}</Label>
            <div className="p-3 bg-muted rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span>{t("Packages Count")}:</span>
                <span className="font-medium">
                  {pickupData.packages?.length || 0}
                </span>
              </div>
              {pickupData.note && (
                <div className="text-sm">
                  <span className="font-medium">{t("Note")}:</span>
                  <p className="text-muted-foreground mt-1">
                    {pickupData.note}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Status Selection */}
          <div className="space-y-2">
            <Label htmlFor="status" className="text-sm font-medium">
              {t("New Status")} *
            </Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder={t("Select pickup status")} />
              </SelectTrigger>
              <SelectContent>
                {PICKUP_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${option.color}`} />
                      <div className="flex flex-col">
                        <span className="font-medium">{t(option.label)}</span>
                        <span className="text-xs text-muted-foreground">
                          {t(option.description)}
                        </span>
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Preview */}
          {selectedStatusOption && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t("Status Preview")}
              </Label>
              <div className="p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`w-3 h-3 rounded-full ${selectedStatusOption.color}`}
                  />
                  <span className="font-medium">
                    {t(selectedStatusOption.label)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t(selectedStatusOption.description)}
                </p>
              </div>
            </div>
          )}

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note" className="text-sm font-medium">
              {t("Note")} ({t("optional")})
            </Label>
            <Textarea
              id="note"
              placeholder={t("Add a note about this status update...")}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleStatusUpdate}
            disabled={
              updatePickupStatusMutation.isPending ||
              selectedStatus === pickupData.status
            }
          >
            {updatePickupStatusMutation.isPending
              ? t("Updating...")
              : t("Update Status")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LivreurPickupStatusUpdateDialog;
